'use client';

import React from 'react';
import Link from 'next/link';
import { BlogPost } from '../../../../shared/models';
import Image from 'next/image';
import OptimizedImage from '../OptimizedImage';
import { getOptimizedImageUrl } from '../../../utils/imageUtils';

interface BlogSectionProps {
    blogPosts?: BlogPost[];
}

const BlogSection: React.FC<BlogSectionProps> = ({ blogPosts = [] }) => {
    // Use the first 4 blog posts or default to empty array if undefined
    const displayPosts = blogPosts.slice(0, 4);

    // First post is the featured post, next 3 are the smaller posts
    const featuredPost = displayPosts[0];
    const smallerPosts = displayPosts.slice(1, 4);

    const optimizedFeaturedPost = getOptimizedImageUrl(featuredPost.HeadlineImageUrl, 1600, 85);

    return (
        <section className="py-16 bg-[#1C1C34]">
            <div className="container mx-auto px-4 md:px-6">
                <h2 className="text-l text-white font-['Inter']">LITTY BLOG</h2>
                <h3 className="text-4xl text-white font-bold mb-4 font-['Inter']">Inside Tips & NYC Venue Guides</h3>

                {/* Featured Blog Post */}
                {blogPosts.length > 0 ? (
                    <div className="mb-12 pt-4">
                        <Link href={featuredPost ? `/blog/${featuredPost.ID}` : '/blog/beyond-the-lounge'} className="block">
                            <div className="overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer">
                                <div className="grid grid-cols-1 md:grid-cols-2">
                                    {/* Left side - Image */}
                                    <div className="aspect-video relative">
                                        {featuredPost?.HeadlineImageUrl && (
                                            <OptimizedImage
                                                src={optimizedFeaturedPost}
                                                alt={featuredPost.HeadlineImageAltText || featuredPost.Title || 'Featured blog post'}
                                                fill
                                                className="object-cover"
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 md:hidden rounded-2xl"></div>
                                    </div>

                                    {/* Right side - Content */}
                                    <div className="p-8 flex flex-col justify-center">
                                        <h4 className="text-2xl font-bold text-white mb-4">{featuredPost?.Title || 'Beyond the Lounge: Aura 57\'s VIP Suites Reimagine Private Events'}</h4>
                                        <p className="text-gray-300 mb-6">{featuredPost?.Excerpt || 'A nightlife destination that seamlessly blends luxury, privacy, and entertainment.'}</p>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </div>
                ) : (
                    <div className="mb-12">
                        <Link href="/blog/beyond-the-lounge" className="block">
                            <div className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer">
                                <div className="grid grid-cols-1 md:grid-cols-2">
                                    {/* Left side - Image */}
                                    <div className="aspect-video relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60 md:hidden rounded-2xl"></div>
                                    </div>

                                    {/* Right side - Content */}
                                    <div className="p-8 flex flex-col justify-center">
                                        <h4 className="text-2xl font-bold text-white mb-4">Beyond the Lounge: Aura 57's VIP Suites Reimagine Private Events</h4>
                                        <p className="text-gray-300 mb-6">A nightlife destination that seamlessly blends luxury, privacy, and entertainment.</p>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </div>
                )}

                {/* Three Smaller Blog Posts */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    {blogPosts.length > 1 ? (
                        // Dynamic blog posts from Firebase
                        smallerPosts.map((post) => (
                            <Link key={post.ID} href={`/blog/${post.ID}`} className="block transition-all duration-300 hover:scale-[1.05]">
                                <div className="cursor-pointer">
                                    <div className="aspect-[16/9] skeleton relative rounded-2xl overflow-hidden mb-4">
                                        {post.HeadlineImageUrl && (
                                            <OptimizedImage
                                                src={post.HeadlineImageUrl}
                                                alt={post.HeadlineImageAltText || post.Title || 'Blog post'}
                                                fill
                                                className="object-cover"
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                                    </div>
                                    <div className="pl-1">
                                        <h4 className="text-xl md:text-2xl font-bold text-white mb-2">{post.Title}</h4>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        // Fallback static content if no posts are available
                        <>
                            {/* Blog Post 1 */}
                            <Link href="/blog/nyc-birthday-ideas" className="block transition-all duration-300 hover:scale-[1.05]">
                                <div className="cursor-pointer">
                                    <div className="aspect-[16/9] skeleton relative rounded-2xl overflow-hidden mb-4">
                                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                                    </div>
                                    <div className="pl-1">
                                        <h4 className="text-xl md:text-2xl font-bold text-white mb-2">NYC Birthday Ideas: 8 Activities & Trendy Bars to Celebrate</h4>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </Link>

                            {/* Blog Post 2 */}
                            <Link href="/blog/nyc-50th-birthday" className="block transition-all duration-300 hover:scale-[1.05]">
                                <div className="cursor-pointer">
                                    <div className="aspect-[16/9] skeleton relative rounded-2xl overflow-hidden mb-4">
                                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                                    </div>
                                    <div className="pl-1">
                                        <h4 className="text-xl md:text-2xl font-bold text-white mb-2">NYC 50th Birthday Ideas and Venues: 8 Perfect Spots</h4>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </Link>

                            {/* Blog Post 3 */}
                            <Link href="/blog/trendy-30th-birthday-places" className="block transition-all duration-300 hover:scale-[1.05]">
                                <div className="cursor-pointer">
                                    <div className="aspect-[16/9] skeleton relative rounded-2xl overflow-hidden mb-4">
                                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                                    </div>
                                    <div className="pl-1">
                                        <h4 className="text-xl md:text-2xl font-bold text-white mb-2">5 Trendy Places for your 30th Birthday Party</h4>
                                        <span className="text-[#C0C0DD] text-sm font-medium">
                                            READ MORE
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </>
                    )}
                </div>

                <div className="text-center mt-8">
                    <p className="mb-4 text-gray-300 text-sm">Get expert advice on planning and booking the best event spaces NYC.</p>
                    <Link href="/blog" className="bg-[#C0C0DD] hover:bg-[#AAAAC8] text-black px-8 py-3 rounded-full text-lg font-medium inline-block font-['Barlow'] whitespace-nowrap">
                        VIEW BLOG
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BlogSection; 